import { useState, useEffect, useMemo } from 'react';
import { X, Plus, Minus, Check } from 'lucide-react';
import { getProductImageUrl } from '../../utils/productImage';
import {
  buildOptionsPayload,
  computeOptionsExtraPriceForProduit,
  formatOptionsSummary,
  getDefaultOptionSelections,
  isOptionMulti,
  getMaxSelectableForOption,
} from '../../utils/caisseProduct';
import { validateOptionsSelectionDetailed } from '../../utils/optionsPricing';

export default function CaisseOptionsModal({ produit, fmt, onClose, onAddToPanier }) {
  const [quantity, setQuantity] = useState(1);
  const [selectedSingleOptions, setSelectedSingleOptions] = useState({});
  const [selectedMultiOptionCounts, setSelectedMultiOptionCounts] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!produit) return;
    const { defaultSingles, defaultMulti } = getDefaultOptionSelections(produit);
    setSelectedSingleOptions(defaultSingles);
    setSelectedMultiOptionCounts(defaultMulti);
    setQuantity(1);
    setError('');
  }, [produit]);

  const basePrice = useMemo(
    () => (produit?.prixPromo != null ? produit.prixPromo : produit?.prix) || 0,
    [produit]
  );

  // Supplément total de la ligne (nbInclus × quantité déjà pris en compte)
  const optionsExtra = useMemo(
    () =>
      computeOptionsExtraPriceForProduit(
        produit,
        selectedSingleOptions,
        selectedMultiOptionCounts,
        quantity
      ),
    [produit, selectedSingleOptions, selectedMultiOptionCounts, quantity]
  );
  const totalPrice = basePrice * quantity + optionsExtra;
  const unitPrice = totalPrice / quantity;

  if (!produit) return null;

  const imageUrl = getProductImageUrl(produit);
  const options = (produit.options || []).filter(
    (o) => Array.isArray(o.choix) && o.choix.length > 0
  );

  const handleMultiAdjust = (option, choixId, delta) => {
    setSelectedMultiOptionCounts((prev) => {
      const counts = { ...(prev[option.id] || {}) };
      const current = counts[choixId] || 0;
      const max = getMaxSelectableForOption(option);
      const totalSelected = Object.values(counts).reduce((s, v) => s + (Number(v) || 0), 0);

      if (delta > 0 && max < Infinity && totalSelected >= max) return prev;

      const next = current + delta;
      if (next <= 0) delete counts[choixId];
      else counts[choixId] = next;

      return { ...prev, [option.id]: counts };
    });
  };

  const handleConfirm = () => {
    const errors = validateOptionsSelectionDetailed(
      produit,
      selectedSingleOptions,
      selectedMultiOptionCounts
    );
    if (errors.length > 0) {
      setError(errors.map((e) => e.message).join(' · '));
      return;
    }

    const payload = buildOptionsPayload(
      produit,
      quantity,
      selectedSingleOptions,
      selectedMultiOptionCounts
    );
    const summary = formatOptionsSummary(payload);

    onAddToPanier({
      produit,
      quantite: quantity,
      prixUnitaire: unitPrice,
      options: payload,
      optionsLabel: summary,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {imageUrl && (
              <img src={imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
            )}
            <div className="min-w-0">
              <h2 className="font-bold text-slate-900 truncate">{produit.nom}</h2>
              <p className="text-emerald-600 font-bold">{fmt(totalPrice)}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {options.length === 0 && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Ce produit n&apos;a pas d&apos;options configurées en ligne. Vérifiez la fiche produit dans
              l&apos;admin (section Options &amp; variantes).
            </p>
          )}

          {options.map((option) => {
            const choices = option.choix.filter((c) => c.actif !== false);
            const isMulti = isOptionMulti(option);
            const counts = selectedMultiOptionCounts[option.id] || {};

            return (
              <div key={option.id}>
                <p className="text-sm font-semibold text-slate-900 mb-2">
                  {option.nom}
                  {option.obligatoire && <span className="text-red-500 ml-1">*</span>}
                </p>
                <div className="space-y-2">
                  {choices.map((choix) => {
                    if (isMulti) {
                      const count = counts[choix.id] || 0;
                      return (
                        <div
                          key={choix.id}
                          className={`flex items-center justify-between p-3 rounded-xl border-2 ${
                            count > 0
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-slate-200'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-slate-900">{choix.nom}</p>
                            {choix.prix > 0 && (
                              <p className="text-xs text-slate-500">+{fmt(choix.prix)}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <button
                              type="button"
                              onClick={() => handleMultiAdjust(option, choix.id, -1)}
                              disabled={count <= 0}
                              className="p-2 rounded-lg border border-slate-200 disabled:opacity-30"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="w-6 text-center font-bold">{count}</span>
                            <button
                              type="button"
                              onClick={() => handleMultiAdjust(option, choix.id, 1)}
                              className="p-2 rounded-lg border border-slate-200"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    }

                    const selected = selectedSingleOptions[option.id] === choix.id;
                    return (
                      <button
                        key={choix.id}
                        type="button"
                        onClick={() =>
                          setSelectedSingleOptions((prev) => ({
                            ...prev,
                            [option.id]: choix.id,
                          }))
                        }
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition ${
                          selected
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            selected ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'
                          }`}
                        >
                          {selected && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className="font-medium text-sm text-slate-900 flex-1">{choix.nom}</span>
                        {choix.prix > 0 && (
                          <span className="text-xs text-slate-500">+{fmt(choix.prix)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-between py-2">
            <span className="font-semibold text-slate-700">Quantité</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="p-2 rounded-lg border border-slate-200"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-8 text-center font-bold text-lg">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="p-2 rounded-lg border border-slate-200"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 shrink-0">
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full py-4 rounded-xl bg-emerald-600 text-white font-bold text-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
          >
            <Plus className="h-6 w-6" />
            Ajouter — {fmt(totalPrice)}
          </button>
        </div>
      </div>
    </div>
  );
}
